// useFeaturePlanStatusPolling.js
import { useEffect, useRef } from "react";
import api from "./api";

/**
 * Custom hook for polling feature plan status
 * @param {Object} options
 * @param {string|null} options.planId - The plan ID to poll
 * @param {boolean} options.enabled - Whether polling should be active
 * @param {Function} options.onUpdate - Callback called on each successful poll with status data
 * @param {Function} options.onTerminal - Callback called when polling stops (completed/failed/deferred)
 * @param {Function} [options.onError] - Optional callback for API errors
 */
export function useFeaturePlanStatusPolling({
  planId,
  enabled,
  onUpdate,
  onTerminal,
  onError,
}) {
  const intervalRef = useRef(null);
  const pollCountRef = useRef(0);
  const startTimeRef = useRef(null);
  const currentIntervalMsRef = useRef(2000);

  // Reset state when planId changes
  useEffect(() => {
    if (planId) {
      pollCountRef.current = 0;
      startTimeRef.current = Date.now();
      currentIntervalMsRef.current = 2000;
    }
  }, [planId]);

  useEffect(() => {
    // Don't poll if disabled or no planId
    if (!enabled || !planId) {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await api.get(`/feature-plans/${planId}/status`);
        const statusData = response.data;

        // Check for terminal states
        const isCompleted = statusData.status === "completed";
        const isFailed = statusData.status === "failed";
        const isDeferred =
          statusData.status === "pending" &&
          statusData.error?.code === "REDIS_UNAVAILABLE";

        // Check hard cap: 5 minutes (300000ms)
        const elapsed = Date.now() - startTimeRef.current;
        const exceededTimeLimit = elapsed >= 300000;

        if (isCompleted || isFailed || isDeferred || exceededTimeLimit) {
          // Stop polling
          if (intervalRef.current) {
            clearTimeout(intervalRef.current);
            intervalRef.current = null;
          }

          // Call terminal callback with reason
          if (onTerminal) {
            let reason = "unknown";
            if (isCompleted) reason = "completed";
            else if (isFailed) reason = "failed";
            else if (isDeferred) reason = "deferred";
            else if (exceededTimeLimit) reason = "timeout";

            onTerminal(statusData, reason);
          }
          return;
        }

        // Update backoff interval based on poll count
        pollCountRef.current += 1;
        if (pollCountRef.current === 3) {
          // After 3 polls, switch to 4s
          currentIntervalMsRef.current = 4000;
        } else if (pollCountRef.current === 6) {
          // After 6 polls total, switch to 8s max
          currentIntervalMsRef.current = 8000;
        }

        // Call update callback
        if (onUpdate) {
          onUpdate(statusData);
        }

        // Schedule next poll with current interval
        if (intervalRef.current) {
          clearTimeout(intervalRef.current);
        }
        intervalRef.current = setTimeout(pollStatus, currentIntervalMsRef.current);
      } catch (err) {
        // Handle errors without throwing
        if (onError) {
          onError(err);
        } else if (onUpdate) {
          // Surface error via onUpdate if no onError handler
          onUpdate({
            error: {
              message:
                err.response?.data?.error ||
                err.message ||
                "Failed to fetch plan status",
            },
          });
        }

        // Continue polling even on error (with current interval)
        if (intervalRef.current) {
          clearTimeout(intervalRef.current);
        }
        intervalRef.current = setTimeout(pollStatus, currentIntervalMsRef.current);
      }
    };

    // Start polling immediately
    pollStatus();

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [planId, enabled, onUpdate, onTerminal, onError]);
}

